import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useContent } from "@/context/ContentContext";
import { Paperclip, FileText, Download, X } from "lucide-react";

export const ACCEPT = "image/png,image/jpeg,image/gif,image/webp,.pdf,.txt,.csv,.doc,.docx,.xls,.xlsx";
export const MAX_FILES = 5;
export const MAX_MB = 10;

// Upload each selected file, binding it to the given message. Fire sequentially.
export async function uploadTicketFiles(files, uploadPath, messageId) {
  for (const file of files) {
    const fd = new FormData();
    fd.append("file", file);
    await api.post(`${uploadPath}?message_id=${messageId}`, fd);
  }
}

// Controlled file picker: paperclip button + selected-file chips (pre-send).
export function AttachmentPicker({ files, setFiles, disabled, idPrefix = "att" }) {
  const { t } = useContent();
  const onPick = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    const merged = [...files];
    for (const f of picked) {
      if (merged.length >= MAX_FILES) { toast.error(t("att.max_files", "Maximum {n} fichiers").replace("{n}", MAX_FILES)); break; }
      if (f.size > MAX_MB * 1024 * 1024) { toast.error(`${f.name} — ${t("att.too_big", "fichier trop volumineux")} (${MAX_MB} Mo)`); continue; }
      merged.push(f);
    }
    setFiles(merged);
  };
  const remove = (i) => setFiles(files.filter((_, idx) => idx !== i));
  return (
    <div className="flex flex-wrap items-center gap-2">
      <label data-testid={`${idPrefix}-picker`} className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs text-muted-foreground hover:bg-secondary/50 ${disabled ? "pointer-events-none opacity-50" : ""}`}>
        <Paperclip className="h-3.5 w-3.5" /> {t("chat.attach", "Joindre")}
        <input type="file" multiple accept={ACCEPT} className="hidden" onChange={onPick} disabled={disabled} data-testid={`${idPrefix}-input`} />
      </label>
      {files.map((f, i) => (
        <span key={i} data-testid={`${idPrefix}-chip-${i}`} className="inline-flex items-center gap-1.5 rounded-full bg-secondary/60 px-2.5 py-1 text-xs">
          <FileText className="h-3 w-3" /> <span className="max-w-[140px] truncate">{f.name}</span>
          <button type="button" onClick={() => remove(i)} data-testid={`${idPrefix}-remove-${i}`}><X className="h-3 w-3 opacity-60 hover:opacity-100" /></button>
        </span>
      ))}
    </div>
  );
}

// Render one attachment: image thumbnail (blob) or a download chip. All fetches
// go through the authenticated backend serve endpoint (no public URLs).
function AttachmentItem({ att, serveBase }) {
  const { t } = useContent();
  const [url, setUrl] = useState(null);
  const isImg = (att.content_type || "").startsWith("image/");
  useEffect(() => {
    let dead = false; let blobUrl;
    if (isImg) {
      api.get(`${serveBase}/${att.id}`, { responseType: "blob" }).then((r) => {
        if (dead) return; blobUrl = URL.createObjectURL(r.data); setUrl(blobUrl);
      }).catch(() => {});
    }
    return () => { dead = true; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [att.id, isImg, serveBase]);
  const download = async () => {
    try {
      const r = await api.get(`${serveBase}/${att.id}`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = u; a.download = att.filename || "fichier"; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(u);
    } catch { toast.error(t("att.download_err", "Téléchargement impossible")); }
  };
  if (isImg) {
    return (
      <button onClick={download} data-testid={`attachment-${att.id}`} title={att.filename} className="block">
        {url ? <img src={url} alt={att.filename} className="max-h-40 max-w-[220px] rounded-lg border border-border object-cover" />
             : <div className="h-24 w-32 animate-pulse rounded-lg bg-secondary" />}
      </button>
    );
  }
  return (
    <button onClick={download} data-testid={`attachment-${att.id}`} className="inline-flex items-center gap-2 rounded-lg border border-border bg-secondary/40 px-3 py-2 text-xs hover:bg-secondary/70">
      <FileText className="h-4 w-4 shrink-0" /> <span className="max-w-[160px] truncate">{att.filename}</span> <Download className="h-3.5 w-3.5 opacity-60" />
    </button>
  );
}

export function AttachmentList({ attachments, serveBase }) {
  if (!attachments?.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2" data-testid="attachment-list">
      {attachments.map((a) => <AttachmentItem key={a.id} att={a} serveBase={serveBase} />)}
    </div>
  );
}
